import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'
import { apiFetch } from '../../lib/api'
import { getToken } from '../../lib/auth'
import { formatOfficerDate } from '../../lib/officerFormat'
import SourceDocumentList from '../documents/SourceDocumentList'
import type { ViewScope } from '../navigation/context'
import { useRead } from '../navigation/useRead'
import { ErrorState } from '../state'
import { recoveryHref, type RecoveryItem } from './recovery'
import {
  parseSourceQuestion,
  parseSourceRequests,
  sourceQuestionPath,
  sourceRequestPath,
  type SourceQuestionRead,
} from './sourceQuestion'

const originalLabels: Readonly<Record<string, string>> = {
  commitment_based: 'Funding structure',
  category: 'Instrument category',
  instrument_type: 'Source description',
  currency: 'Currency',
  total_issued: 'Reported total units',
  total_commitments: 'Reported aggregate commitments',
  balance_as_of: 'Source balance date',
  balance_date_source: 'Balance date quotation',
  balance_date_role: 'Balance date meaning',
  balance_date_context: 'Balance date context',
  nominal_value: 'Reported nominal value',
  nominal_value_source: 'Nominal value quotation',
  stated_capital: 'Reported share capital',
  stated_capital_currency: 'Share capital currency',
  stated_capital_source: 'Share capital quotation',
  units_held: 'Proposed position quantity',
  units_delta: 'Proposed movement quantity',
  consideration_amount: 'Proposed consideration',
  consideration_currency: 'Consideration currency',
  effective_date: 'Proposed date',
  consideration: 'Proposed consideration',
  nominal_amount: 'Proposed nominal amount',
  nominal_amount_source: 'Nominal amount source',
  date_facts: 'Proposed date evidence',
  trade_name: 'Proposed movement name',
  unverified_facts: 'Facts still unverified',
  distribution_subtype: 'Proposed distribution type',
  transaction_type: 'Proposed operation',
}

export default function SourceQuestionReview({
  item,
  scope,
  refresh,
  onChanged,
}: {
  item: RecoveryItem
  scope: ViewScope
  refresh: number
  onChanged: () => void
}) {
  const [reload, setReload] = useState(0)
  const read = useRead<unknown>(sourceQuestionPath(scope, item.task_id), refresh + reload)
  const parsed = useMemo(() => {
    if (read.loading) return {}
    if (read.error) return { error: read.error }
    try {
      return { view: parseSourceQuestion(read.value, item) }
    } catch (error) {
      return { error }
    }
  }, [read.value, read.loading, read.error, item])
  const view = parsed.view
  return (
    <section
      aria-label={
        item.source_question_kind === 'multiclass'
          ? 'Share-class source question'
          : 'Source questions'
      }
    >
      {read.loading ? <p role="status">Reading the source question…</p> : null}
      {parsed.error ? (
        <ErrorState
          compact
          what="source question"
          error={parsed.error}
          onRetry={() => setReload((n) => n + 1)}
        />
      ) : null}
      {view ? (
        <>
          {!view.question.complete ? (
            <p role="alert">
              The retained question does not yet have complete current source support. No request
              can be prepared from this view.
            </p>
          ) : (
            <>
              <p>
                No allocation or movement has been booked for these questions. The values below are
                proposals to review against the cited source.
              </p>
              {view.question.questions.map((question) => (
                <article key={`${question.publication_id ?? 'legacy'}:${question.ordinal}`}>
                  <h4>{question.instrument_name}</h4>
                  <p>{question.prompt}</p>
                  {question.holder_name ? <p>Holder: {question.holder_name}</p> : null}
                  {question.issuer_name ? <p>Issuer: {question.issuer_name}</p> : null}
                  <dl>
                    {Object.entries(question.original_values).map(([key, value]) => (
                      <div key={key}>
                        <dt>{originalLabels[key] ?? key}</dt>
                        <dd>
                          {value == null
                            ? 'Unknown'
                            : key === 'effective_date' || key === 'balance_as_of'
                              ? formatOfficerDate(String(value))
                              : typeof value === 'object'
                                ? JSON.stringify(value)
                                : String(value)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                  <blockquote>{(question.source_citation ?? question.citation).quote}</blockquote>
                  <SourceDocumentList
                    documents={[question.document]}
                    context={{ fundId: scope.fund_id, returnTo: recoveryHref(scope, item.task_id) }}
                  />
                  {question.class_context ? (
                    <section aria-label="Classes named on this page">
                      <p>Classes named on this page</p>
                      <blockquote>{question.class_context.citation.quote}</blockquote>
                      <p>
                        This identifies the class wording only. It does not establish an allocation.
                      </p>
                      <SourceDocumentList
                        documents={[question.class_context.document]}
                        context={{
                          fundId: scope.fund_id,
                          returnTo: recoveryHref(scope, item.task_id),
                        }}
                      />
                    </section>
                  ) : null}
                </article>
              ))}
              {scope.kind === 'fund' && scope.fund_id ? (
                <RequestDraft
                  key={view.read_revision}
                  view={view}
                  fundId={scope.fund_id}
                  onChanged={onChanged}
                />
              ) : (
                <p>
                  Open a Fund view to prepare a request for supporting evidence.{' '}
                  {item.fund_context_ids.map((fundId) => (
                    <Link
                      key={fundId}
                      to={recoveryHref({ kind: 'fund', fund_id: fundId }, item.task_id)}
                    >
                      Open Fund work →{' '}
                    </Link>
                  ))}
                </p>
              )}
            </>
          )}
        </>
      ) : null}
    </section>
  )
}

function RequestDraft({
  view,
  fundId,
  onChanged,
}: {
  view: SourceQuestionRead
  fundId: string
  onChanged: () => void
}) {
  const [description, setDescription] = useState('')
  const [counterparty, setCounterparty] = useState('')
  const [pending, setPending] = useState(false)
  const [reload, setReload] = useState(0)
  const [error, setError] = useState<unknown>(null)
  const active = useRef(false),
    inFlight = useRef(false)
  const attempt = useRef<{ input: string; id: string } | null>(null)
  const path = sourceRequestPath(view.task_id)
  const saved = useRead<unknown>(path + '?fund_id=' + encodeURIComponent(fundId), reload)
  const parsed = useMemo(() => {
    if (saved.loading) return {}
    if (saved.error) return { error: saved.error }
    try {
      return { value: parseSourceRequests(saved.value, view.task_id) }
    } catch (error) {
      return { error }
    }
  }, [saved.value, saved.error, saved.loading, view.task_id])
  useEffect(() => {
    active.current = true
    return () => {
      active.current = false
    }
  }, [])
  async function submit(event: FormEvent) {
    event.preventDefault()
    if (inFlight.current || !parsed.value?.complete || !description.trim() || !counterparty.trim())
      return
    const account = getToken()
    if (!account) return
    const input = JSON.stringify({
      revision: view.read_revision,
      fund_id: fundId,
      requested_document: description.trim(),
      counterparty_name: counterparty.trim(),
    })
    if (attempt.current?.input !== input) attempt.current = { input, id: crypto.randomUUID() }
    const command = attempt.current
    inFlight.current = true
    setPending(true)
    setError(null)
    try {
      const raw = await apiFetch<unknown>(path, {
        method: 'POST',
        body: JSON.stringify({ ...JSON.parse(input), command_id: command.id }),
      })
      const result = parseSourceRequests({ complete: true, requests: [raw] }, view.task_id)
        .requests[0]
      if (
        result.request_id !== command.id ||
        result.requested_document !== description.trim() ||
        result.counterparty_name !== counterparty.trim() ||
        result.earlier_review
      )
        throw new Error('The draft could not be verified. Refresh before continuing.')
      if (!active.current || getToken() !== account) return
      setReload((n) => n + 1)
      onChanged()
    } catch (failure) {
      if (active.current && getToken() === account) setError(failure)
    } finally {
      inFlight.current = false
      if (active.current && getToken() === account) setPending(false)
    }
  }
  return (
    <div>
      {saved.loading ? <p role="status">Reading saved requests…</p> : null}
      {parsed.error ? (
        <ErrorState
          compact
          what="saved requests"
          error={parsed.error}
          onRetry={() => setReload((n) => n + 1)}
        />
      ) : null}
      {parsed.value && !parsed.value.complete ? (
        <p role="alert">
          Saved requests could not be verified. Refresh before preparing another draft.
        </p>
      ) : null}
      {parsed.value?.requests.map((request) => (
        <p key={request.request_id}>
          Draft saved: {request.requested_document} · {request.counterparty_name}. Not sent; this
          question remains open.
          {request.earlier_review ? ' Prepared against an earlier review.' : ''}
        </p>
      ))}
      {parsed.value?.complete ? (
        <form onSubmit={submit} aria-label="Request share-class evidence">
          <label>
            Document needed
            <textarea
              required
              maxLength={2000}
              value={description}
              disabled={pending}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
          <label>
            Counterparty
            <input
              required
              maxLength={500}
              value={counterparty}
              disabled={pending}
              onChange={(e) => setCounterparty(e.target.value)}
            />
          </label>
          <p>This saves a draft. Sending is not yet available for this question.</p>
          <button type="submit" disabled={pending || !description.trim() || !counterparty.trim()}>
            {pending ? 'Saving draft…' : 'Prepare document request'}
          </button>
        </form>
      ) : null}
      {error ? (
        <ErrorState compact what="document request" error={error} onRetry={() => setError(null)} />
      ) : null}
    </div>
  )
}
